/**
 * Content kind constants used throughout the application
 */
export const CONTENT = {
  CODEBASE_ENTRY_POINTS: 'codebase-entry-points',
  CODEBASE_DOMAINS: 'codebase-domains',
  CODEBASE_AUDIENCES: 'codebase-audiences',
  CODEBASE_KINDS: 'codebase-kinds',
  TOP_LEVEL_SHORT_PARAGRAPH: 'TOP_LEVEL_SHORT_PARAGRAPH',
  TOP_LEVEL_SHORT_SENTENCE: 'TOP_LEVEL_SHORT_SENTENCE',
  DEEP_CONTEXT_CHANGELOG: 'deep-context-changelog',
  DEEP_CONTEXT_ARCHITECTURE: 'deep-context-architecture',
  DEEP_CONTEXT_LLM_ONBOARDING: 'deep-context-llm-onboarding',
  DEEP_CONTEXT_BESPOKE: 'deep-context-bespoke',
} as const;

export type ContentKindType = (typeof CONTENT)[keyof typeof CONTENT];

/**
 * Content display title mapping for user-friendly names
 */
export const CONTENT_DISPLAY_TITLES: Record<string, string> = {
  [CONTENT.DEEP_CONTEXT_CHANGELOG]: 'Code History',
  [CONTENT.DEEP_CONTEXT_ARCHITECTURE]: 'System Architecture Overview',
  [CONTENT.DEEP_CONTEXT_LLM_ONBOARDING]: 'Onboarding Guide',
} as const;

/**
 * Hardcoded descriptions for autodocs titles to provide better user experience
 * when the content or metadata doesn't have sufficient descriptions
 */
export const AUTODOCS_DESCRIPTIONS: Record<string, string> = {
  'System Architecture Overview':
    'Describes the fundamental structure, key components, and relationships of a software system optimized for an LLM to consume.',
  'Onboarding Guide':
    'Essential information about a codebase for an LLM to understand, navigate, and contribute to the project.',
  'Code History':
    'A dense document designed to orient an LLM to the codebase, navigate it, identify important components, and the relationships between them.',
} as const;

/**
 * Get the hardcoded title for an autodocs content kind, or fall back to the provided content kind
 */
export const getAutodocsTitle = (contentKind?: ContentKindType): string => {
  if (contentKind) {
    return CONTENT_DISPLAY_TITLES[contentKind as keyof typeof CONTENT_DISPLAY_TITLES] || '';
  }

  return '';
};

/**
 * Get the hardcoded description for an autodocs title, or fall back to the provided description
 */
export const getAutodocsDescription = (title: string, fallbackDescription?: string): string => {
  return AUTODOCS_DESCRIPTIONS[title] || fallbackDescription || 'No description available';
};
